import { HYDRATE } from 'next-redux-wrapper'

import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'

import { Role } from '@/features/managed-accounts'

import type { AppState } from '../store'
import type { HydratePayload } from '../types/hydrate'

export interface MeStateIProps {
  id?: string
  email?: string
  roles?: Role[]
  // @deprecated Use `roles` instead. Kept for backward compatibility.
  roleName?: Role
  firstName?: string
  lastName?: string
  username?: string
}

const initialState: MeStateIProps = null

// Stable empty array reference to avoid unnecessary re-renders
const EMPTY_ROLES: Role[] = []

export const meSlice = createSlice({
  name: 'me',
  initialState,
  reducers: {
    setMe: (_, action: PayloadAction<MeStateIProps>) => {
      return action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      const hydrateAction = action as PayloadAction<
        HydratePayload<MeStateIProps>
      >
      return {
        ...state,
        ...hydrateAction.payload.subject,
      }
    })
  },
})

export const { setMe } = meSlice.actions

export const selectMe = (state: AppState) => state.me
export const selectFullName = (state: AppState) =>
  `${state.me?.firstName ?? ''} ${state.me?.lastName ?? ''}`
export const selectEmail = (state: AppState) => state.me?.email
export const selectUserId = (state: AppState) => state.me?.id
// Falls back to deprecated `roleName` for backward compatibility
// Memoized to maintain referential equality and prevent unnecessary re-renders
export const selectRoles = createSelector([selectMe], (me): Role[] => {
  if (me?.roles?.length) {
    return me.roles
  }
  if (me?.roleName) {
    return [me.roleName]
  }
  return EMPTY_ROLES
})
// For backward-compatible display, returns first role
export const selectPrimaryRole = (state: AppState): Role | undefined =>
  state.me?.roles?.[0] ?? state.me?.roleName

// Default export for reducers is a pattern recommended by Redux
// See here: <https://redux.js.org/tutorials/fundamentals/part-3-state-actions-reducers#combinereducers>
// eslint-disable-next-line import/no-default-export
export default meSlice.reducer
